import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import type { ZodType } from 'zod';

type FetchBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>;

export type OpenApiEndpointSchemaMap = Record<string, { responses: Record<string | number, ZodType> }>;

function validationError(endpointName: string, status: number, data: unknown): FetchBaseQueryError {
    return {
        status: 'CUSTOM_ERROR',
        error: `OpenAPI response validation failed for ${endpointName} (${status})`,
        data,
    };
}

async function validateResponse(
    endpointSchemas: OpenApiEndpointSchemaMap,
    endpointName: string,
    status: number,
    data: unknown
): Promise<{ success: true; data: unknown } | { success: false; error: FetchBaseQueryError }> {
    const endpointSchema = endpointSchemas[endpointName];

    if (endpointSchema === undefined) {
        return {
            success: false,
            error: validationError(endpointName, status, {
                reason: 'MISSING_ENDPOINT_SCHEMA',
            }),
        };
    }

    const responseSchema =
        endpointSchema.responses[status] ??
        endpointSchema.responses[String(status)] ??
        endpointSchema.responses.default;

    if (responseSchema === undefined) {
        return {
            success: false,
            error: validationError(endpointName, status, {
                reason: 'MISSING_RESPONSE_SCHEMA',
            }),
        };
    }

    const parsed = await responseSchema.safeParseAsync(data);

    if (!parsed.success) {
        return {
            success: false,
            error: validationError(endpointName, status, {
                reason: 'SCHEMA_VALIDATION_FAILED',
                issues: parsed.error.issues,
            }),
        };
    }

    return { success: true, data: parsed.data };
}

/**
 * Validates every HTTP response against typed-openapi's generated Zod schema.
 * Missing endpoint and status schemas fail closed.
 */
export function withOpenApiResponseValidation(
    baseQuery: FetchBaseQuery,
    endpointSchemas: OpenApiEndpointSchemaMap
): FetchBaseQuery {
    return async (args, api, extraOptions) => {
        const result = await baseQuery(args, api, extraOptions);
        const errorStatus = result.error?.status;

        if (errorStatus !== undefined && typeof errorStatus !== 'number') {
            return result;
        }

        const status = errorStatus ?? result.meta?.response?.status;

        if (status === undefined) {
            return {
                error: validationError(api.endpoint, 0, {
                    reason: 'MISSING_HTTP_STATUS',
                }),
                meta: result.meta,
            };
        }

        const validated = await validateResponse(
            endpointSchemas,
            api.endpoint,
            status,
            result.error ? result.error.data : result.data
        );

        if (!validated.success) {
            return { error: validated.error, meta: result.meta };
        }

        if (result.error) {
            return {
                ...result,
                error: {
                    ...result.error,
                    data: validated.data,
                },
            };
        }

        return { ...result, data: validated.data };
    };
}
